import { IBlog, IBlogItem } from "@Interfaces/Api/blog";
import { Blog } from "Services/API/IndividualApi's/Blog";
import { notFound } from "next/navigation";
import Image from "next/image";
import {
  Container,
  Box,
  Typography,
  Chip, Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import { GetStaticProps } from "next";
import { Layout } from "@Components/Layout";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NextLink from "next/link";
import { StyledHeadingWithGradientunderline } from "@Styled/Pages/Landing/Category";
import { StyledCategoryFilterContainer } from "@Styled/Pages/Category";
import Head from "next/head";

interface Props {
  blog: IBlogItem
}

export default function BlogDetailPage({ blog }: Props) {

  if (!blog) return notFound();

  return (
    <>
      <Head>
        <title>{blog.metaData?.title || blog.title}</title>
        <meta name="description" content={blog.metaData?.description || blog.excerpt} />
      </Head>
      <Layout>
        <Container maxWidth="md" sx={{ py: { sm: 4, xs: 8 } }}>

          {/* Breadcrumb */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{ mb: 4 }}
          >
            <MuiLink
              component={NextLink}
              href="/"
              underline="hover"
              color="inherit"
            >
              Home
            </MuiLink>

            <MuiLink
              component={NextLink}
              href="/blog"
              underline="hover"
              color="inherit"
            >
              Blog
            </MuiLink>

            <Typography color="text.primary">
              {blog.title}
            </Typography>
          </Breadcrumbs>

          {/* Cover Image */}
          {blog.coverImage?.url && (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: 320,
                mb: 5,
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <Image
                src={blog.coverImage.url}
                alt={blog.title}
                fill
                style={{ objectFit: "cover" }}
              />
            </Box>
          )}

          {/* Title */}
          <StyledCategoryFilterContainer>
            <StyledHeadingWithGradientunderline
              variant="h1"
              sx={{
                fontSize: { sm: "1.5rem", xs: "1.2rem" },
                letterSpacing: "-1px",
              }}
            >{blog.title}{" "}</StyledHeadingWithGradientunderline>
          </StyledCategoryFilterContainer>

          {/* Published Date */}
          {blog.publishedAt && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              {new Date(blog.publishedAt).toDateString()}
            </Typography>
          )}

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mb: 4,
              }}
            >
              {blog.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  color="success"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          )}

          {/* Content */}
          <Box
            sx={{
              "& img": { maxWidth: "100%", borderRadius: 2 },
              "& p": { mb: 2, lineHeight: 1.8 },
              "& h2": { mt: 4, mb: 2, fontWeight: 600 },
              "& h3": { mt: 3, mb: 1.5, fontWeight: 600 },
              "& ul": { pl: 3, mb: 2 },
              "& li": { mb: 1 },
            }}
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

        </Container>

      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  if (!params?.slug) {
    return {
      notFound: true,
    };
  }
  const blog: IBlogItem | null = await Blog.getBySlug(params.slug);
  return {
    props: {
      blog
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 10 seconds
    revalidate: 3, // In seconds
  };
}

export async function getStaticPaths() {
  try {
    const apiResponse = await Blog.getPublished();
    const response: IBlog = apiResponse?.result || apiResponse;
    const posts = response?.data || [];

    const paths = posts.map((post) => ({
      params: { slug: post.slug },
    }));

    return {
      paths,
      fallback: "blocking",
    };
  } catch (error) {
    return {
      paths: [],
      fallback: "blocking",
    };
  }
}
